import {
  TurnState,
  NOCARD,
  COOKIES_ENABLED,
  ENABLE_DEBUGGING,
} from "./constants";
import { UNKNOWN } from "./bot";
import { getLeaderBoardEntry, getTotalPlayerCount, sum, sumRoundScores, getScores, cardRank, presetOf } from "./common";
import firebase from "./fb";
import { isLocalMode } from "./localStore";
import { portalInviteRoom } from "./portal";
import cookies from "browser-cookies";
import { Howl, Howler } from "howler";
import { duckMusic, holdMusic } from "./sound";
import { hasInteracted, onFirstInteraction } from "./firstInteraction";
import { logEvent as logAnalyticsEvent } from "./analytics";
import { isAuthIframeNetworkError } from "./authNetworkError";
import { t, joinNames, Key } from "./i18n";

export { getLeaderBoardEntry, getTotalPlayerCount, sum, sumRoundScores, getScores, NOCARD };

export const getPrettyCardDesc = (value: string) => {
  if (!value) return "";
  if (value === NOCARD) return "";
  if (value === UNKNOWN) return "";

  const suit = value.slice(-1);
  let number = value.slice(0, -1);
  if (value === "XJ" || value === "YJ") {
    return t("card.joker");
  }
  if (number === "K" || number === "Q" || number === "J" || number === "A") {
    number = t(`card.${number}` as Key);
  }
  if (suit === "H" || suit === "C" || suit === "D" || suit === "S") {
    return t("card.of", { rank: number, suit: t(`suit.${suit}` as Key) });
  }
  throw new Error(`Invalid card value ${value}`);
};
export const getCardAction = (value: string, rules?: string) => {
  /**
   * What a drawn card does, from the house rules preset (common/index.ts):
   *
   * Rafiki: Joker, Red King, A-6 = replace; 7-8 = look at own; 9-10 = look at opponent;
   *         Jack, Queen, Black King = blind swap with an opponent.
   * Gringo: 10 = look at own, Jack = look at opponent, Queen = blind swap,
   *         Black King = look at an opponent's card, then swap (see rafiki.ts), everything else = replace.
   * Cambio, Kaboo: 7-8 peek, 9-10 spy, Jack/Queen blind swap, Black King look then swap.
   * Cabo: 7-8 peek, 9-10 spy, Jack/Queen blind swap (the published game has no other powers).
   */
  const preset = presetOf(rules);
  const rank = cardRank(value);
  if (!rank) {
    throw new Error("No turnState for this value");
  }
  if (preset.peek.includes(rank)) return TurnState.LookAtOwn;
  if (preset.spy.includes(rank) || preset.lookAndSwap.includes(rank)) return TurnState.LookAtOpponents;
  if (preset.blindSwap.includes(rank)) return TurnState.SwapWithOpponent;
  return TurnState.Replace;
};

// A "look and swap" card (the black King in Gringo, Cambio and Kaboo): look at an
// opponent's card, then swap.
export const isLookThenSwap = (value: string, rules?: string) => {
  const rank = cardRank(value);
  return Boolean(rank) && presetOf(rules).lookAndSwap.includes(rank as string);
};

const getColor = (card: string) => {
  const suit = card.slice(-1);
  return ["C", "S"].includes(suit) ? "red" : "black";
};

export const isCorrectSlam = (card: string, topDiscard: string): boolean => {
  const result = card.slice(0, -1) === topDiscard.slice(0, -1) || (card[1] === "J" && topDiscard[1] === "J");
  return result;
};

export const generateRoomCode = () => {
  return Math.random().toString(36).substring(2, 6);
};

export const roomCodeFromLocation = () => {
  if (window?.location?.search?.startsWith("?room=")) {
    return window?.location?.search?.substring(6);
  }
  // A portal invite link (see portal.ts). A build that runs its rooms in the browser has
  // nothing to join, so it never follows one.
  if (isLocalMode) return "";
  return portalInviteRoom();
};

export const urlWithRoomCode = (url: string, roomCode?: string) => {
  roomCode = roomCode || roomCodeFromLocation();
  if (roomCode) {
    return `${url}?room=${roomCode}`;
  }
  return url;
};

// "A, B and C" (in the player's language).
export const combineWithAnd = (names: (string | undefined)[]): string => joinNames(names);

export const randomFirstTurn = (playerIds: string[], random: () => number = Math.random) => {
  const count = playerIds.length;
  const index = Math.floor(random() * count);
  return [...playerIds.slice(index), ...playerIds.slice(0, index)];
};

export const cycleFirstTurn = (playerIds: string[], previousFirstPlayer: string) => {
  if (playerIds.length < 2) {
    return playerIds;
  }
  let index = playerIds.indexOf(previousFirstPlayer) + 1;
  if (index === playerIds.length) {
    index = 0;
  }
  if (index === -1) {
    index = 0;
  }
  return [...playerIds.slice(index, playerIds.length), ...playerIds.slice(0, index)];
};

export class InvalidMoveError extends Error {
  constructor(message: string) {
    super(message); // (1)
    this.name = "InvalidMoveError";
  }
}

// The jingle ("let's get freaky with Rafiki", ~8s) is the theme tune: it plays once on the way
// into a game and on the winner's screen; theme_call is its shorter cut for the Rafiki call
// (~6s: from 1.95s in to the end, so the closing "Slammers" line is heard in full; until 4 Nov
// 2026 it stopped just before that line, player feedback). The older calling_rafiki and win clips
// stay in public/sounds but are no longer loaded.
export const SOUND_FILES: string[] = [
  "blind_swap",
  "chat",
  "error",
  "pickup_card",
  "putdown_card",
  "shuffle",
  "slamming",
  "theme",
  "theme_call",
  "tick",
  "turn",
];

// Old sound names still map to what plays now.
export const SOUND_ALIASES: Record<string, string> = {
  calling_rafiki: "theme_call",
  win: "theme",
};
export const resolveSound = (sound: string): string => SOUND_ALIASES[sound] || sound;
// Sounds that duck the portal's music loop while they play.
export const JINGLES = ["theme", "theme_call"];

const sounds: any = {};

// iPhones and iPads mute Web Audio whenever the ringer switch is on silent, which is how most
// phones are carried; HTML5 audio plays regardless (as videos do). So on iOS the effects use
// HTML5 audio. Everywhere else Web Audio is kept for its lower latency.
export const isIOS = (): boolean => {
  try {
    const ua = navigator.userAgent || "";
    return /iP(hone|ad|od)/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  } catch (e) {
    return false;
  }
};

// When the sound files download. The short effects (about 70 KB in all) once the page has
// finished loading, not alongside the game's code and artwork: on a slow connection they used to
// delay the first screen. The two theme tunes (about 170 KB) only on the player's first tap, click
// or key (firstInteraction.ts): nothing can be heard before one, and most visits to the
// home page never play them. A sound played before its file is here loads on demand (Howler
// queues it), so the tap that deals the first game starts the theme's download itself.
SOUND_FILES.forEach((sound) => {
  const s = new Howl({
    // In order of preference
    src: [`${process.env.PUBLIC_URL}/sounds/${sound}.webm`, `${process.env.PUBLIC_URL}/sounds/${sound}.mp3`],
    html5: isIOS(),
    preload: false,
  });
  sounds[sound] = s;
});
const loadSome = (names: string[]) =>
  names.forEach((name) => {
    try {
      const s = sounds[name];
      if (s && s.state() === "unloaded") s.load();
    } catch (e) {}
  });
export const EFFECT_SOUNDS = SOUND_FILES.filter((s) => !JINGLES.includes(s));
let effectsRequested = false;
export const loadEffectSounds = () => {
  if (effectsRequested) return;
  effectsRequested = true;
  loadSome(EFFECT_SOUNDS);
};
let soundsRequested = false;
export const loadSounds = () => {
  if (soundsRequested) return;
  soundsRequested = true;
  effectsRequested = true;
  loadSome(SOUND_FILES);
};
// The start theme, a while after the page has loaded and the browser is idle (about 2.5 s on,
// never during the first load). Since the theme moved to the first tap (#132), the tap on Quick
// Play was often what started its download, so on a slow connection the tune began a moment
// after the deal. Fetched quietly beforehand, it's usually ready by then. Nothing waits on it,
// and it's skipped when the player has asked to save data (the first tap still fetches it).
export const THEME_PREFETCH_DELAY_MS = 2500;
const saveData = (): boolean => {
  try {
    return Boolean((navigator as any)?.connection?.saveData);
  } catch (e) {
    return false;
  }
};
export const prefetchStartTheme = () => {
  if (soundsRequested || saveData()) return;
  loadSome(["theme"]);
};
const whenIdle = (fn: () => void) => {
  const w: any = typeof window !== "undefined" ? window : undefined;
  if (w && typeof w.requestIdleCallback === "function") w.requestIdleCallback(fn, { timeout: 2000 });
  else setTimeout(fn, 0);
};
try {
  const later = () => {
    setTimeout(loadEffectSounds, 300);
    setTimeout(() => whenIdle(prefetchStartTheme), THEME_PREFETCH_DELAY_MS);
  };
  if (typeof document !== "undefined" && document.readyState === "complete") later();
  else if (typeof window !== "undefined") window.addEventListener("load", later, { once: true });
} catch (e) {}
onFirstInteraction(loadSounds);

export const initializeAudio = () => {};

export const playAudio = (sound: string) => {
  const name = resolveSound(sound);
  // The effects normally loaded already (after page load). A sound played before then, or a
  // theme tune before the first tap, loads on demand: Howler fetches it when played.
  loadEffectSounds();
  const howl: Howl | undefined = sounds[name];
  if (!howl) return;
  if (!JINGLES.includes(name)) {
    howl.play();
    return;
  }
  playJingle(howl);
};

// A jingle over the music loop: duck the loop while it plays and bring it back after. The
// restore is also on a timer in case the clip never loads (offline, blocked) and never "ends".
// Only one jingle at a time: a second (Rafiki called while the start theme is still going)
// cuts the first off rather than layering over it.
const playJingle = (howl: Howl, onDone?: () => void) => {
  JINGLES.forEach((j) => {
    if (sounds[j]?.playing()) sounds[j].stop();
  });
  const restore = duckMusic();
  const finish = () => {
    restore();
    if (onDone) onDone();
  };
  let id: number | undefined;
  try {
    id = howl.play();
    howl.once("end", finish, id);
    howl.once("stop", finish, id);
    howl.once("playerror", finish, id);
    if (howl.state() !== "loaded") howl.once("loaderror", finish);
  } catch (e) {
    finish();
    return;
  }
  setTimeout(finish, 12000);
};

// The theme tune at the start of every game: on the tap that deals a Quick Play game, and
// again whenever a new game is dealt (StartTheme: Play again, a game with friends, the Daily).
// Never on page load. A tap and the deal it causes a second later only play it once: anything
// within 10 s of the last start is skipped. Howler's global mute (the site's own toggle, the
// portal's mute, an ad break) still applies. On the portal builds the music loop waits for it.
// (2 Nov 2026: was once per page load, so a second game, or a game with friends after the
// room was made, had no theme.)
let themeStartedAt = 0;
let themeWaitingForTap = false;
export const playStartTheme = () => {
  // A game dealt before the player has touched the page (a new portal player is dealt straight
  // in): browsers can't play it yet, and Howler would only hold it until the first tap. Wait for
  // that tap here instead, so the tune isn't downloaded during the game's first load.
  if (!hasInteracted()) {
    if (!themeWaitingForTap) {
      themeWaitingForTap = true;
      onFirstInteraction(() => {
        themeWaitingForTap = false;
        playStartTheme();
      });
    }
    return;
  }
  const now = Date.now();
  if (now - themeStartedAt < 10000) return;
  themeStartedAt = now;
  loadSounds();
  const howl: Howl | undefined = sounds["theme"];
  if (!howl) return;
  const release = holdMusic();
  playJingle(howl, release);
};

export const generateBotId = () => {
  return Math.random().toString(36).substring(2, 9);
};

export const isOpaqueScriptError = (message: any, source?: string) =>
  String(message || "").trim() === "Script error." && !source;

// An error thrown inside a script served from another site (Google's tag, portal SDKs).
// Google's gtag.js throws "reading 'is_legacy_loaded'" on every page load (seen 1 Nov 2026):
// harmless to players, but it made every session look like it had crashed.
export const isThirdPartyError = (source: any, pageOrigin: string) => {
  const s = String(source || "");
  if (!/^https?:\/\//i.test(s)) return false;
  try {
    return new URL(s).origin !== pageOrigin;
  } catch (e) {
    return false;
  }
};

export const initializeErrorReporting = () => {
  if (typeof window === "undefined") {
    return;
  }
  if (isLocalMode) return;
  window.onerror = (message, source, lineno, colno, error) => {
    // "Script error." with no source is an error inside another site's script (Google's tag,
    // portal SDKs): the browser hides the details, so it tells us nothing and only adds noise.
    if (isOpaqueScriptError(message, source)) return;
    if (isThirdPartyError(source, window.location.origin)) return;
    // Firebase Auth's iframe helper failing to load (iPhones, blocked or flaky network): harmless,
    // handled here so it isn't reported as uncaught (authNetworkError.ts).
    if (isAuthIframeNetworkError(message, error)) return true;
    logAnalyticsEvent("exception", {
      message,
      source,
      lineno,
      colno,
      error,
    });
  };
};

export const verifyCookieSupport = () => {
  return Promise.resolve(cookies.get(COOKIES_ENABLED));
};

export const reportError = (e: Error) => {
  if (ENABLE_DEBUGGING) {
    throw e;
  } else {
    window?._errs?.push(e);
  }
};

export const isAnonymous = () => {
  return !isLocalMode && Boolean(firebase.auth().currentUser?.isAnonymous);
}

export const isInsideIframe = () => {
  return window.self !== window.top
}
